import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle, RotateCcw } from "lucide-react";

import { Button } from "@/components/ui/button";
import { reportFrontendError, type FrontendErrorReport } from "@/lib/global-error-handlers";
import { classifyFrontendError } from "@/lib/frontend-error-diagnostics";

type AppErrorBoundaryProps = {
  children: ReactNode;
};

type AppErrorBoundaryState = {
  error: Error | null;
  componentStack: string;
  report: FrontendErrorReport | null;
};

function normalizeError(error: unknown): Error {
  if (error instanceof Error) return error;
  return new Error(typeof error === "string" ? error : "Unknown application error");
}

export class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
  state: AppErrorBoundaryState = { error: null, componentStack: "", report: null };

  static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
    return { error: normalizeError(error), componentStack: "", report: null };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    const report = reportFrontendError({
      source: "react_boundary",
      error,
      componentStack: info.componentStack || "",
    });
    this.setState({ componentStack: info.componentStack || "", report });
  }

  private reload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const diagnostic = classifyFrontendError(this.state.error, {
      source: "react_boundary",
      componentStack: this.state.componentStack,
    });

    return (
      <main className="genrl-route-canvas flex min-h-screen items-center justify-center px-6 py-10">
        <section className="w-full max-w-3xl rounded-[28px] border border-[var(--genrl-panel-border)] bg-[var(--genrl-panel-bg)] p-8 shadow-[var(--genrl-panel-shadow)] backdrop-blur-2xl sm:p-10">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-destructive/25 bg-destructive/10 text-destructive">
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[var(--genrl-panel-ink-muted)]">
                {diagnostic.code}
              </p>
              <h1 className="mt-3 text-2xl font-semibold tracking-normal text-[var(--genrl-panel-ink)]">
                {diagnostic.title}
              </h1>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-[var(--genrl-panel-ink-soft)]">
                {diagnostic.userMessage}
              </p>
              <div className="mt-5 max-w-2xl rounded-xl border border-[var(--genrl-panel-border)] bg-[var(--genrl-panel-bg-soft)] p-4 text-xs leading-5 text-[var(--genrl-panel-ink-soft)]">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-[var(--genrl-panel-ink)]">Error ID</span>
                  <span className="font-mono">{this.state.report?.id || "logging..."}</span>
                </div>
                <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
                  <span className="font-semibold text-[var(--genrl-panel-ink)]">Route</span>
                  <span className="font-mono">
                    {this.state.report?.path || (typeof window === "undefined" ? "" : window.location.pathname)}
                  </span>
                </div>
                <p className="mt-3 break-words font-mono text-[11px] text-destructive">
                  {diagnostic.technicalMessage}
                </p>
              </div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Button
                  type="button"
                  onClick={this.reload}
                  className="text-white hover:opacity-90"
                  style={{ background: "var(--genrl-panel-accent)" }}
                >
                  <RotateCcw className="h-4 w-4" />
                  Reload
                </Button>
              </div>
              {import.meta.env.DEV ? (
                <pre className="mt-6 max-h-56 overflow-auto rounded-xl border border-[var(--genrl-panel-border)] bg-[var(--genrl-panel-bg-soft)] p-4 text-xs leading-5 text-[var(--genrl-panel-ink-soft)]">
                  {this.state.error.stack || this.state.error.message}
                </pre>
              ) : null}
            </div>
          </div>
        </section>
      </main>
    );
  }
}
